import { Box, HStack, VStack, Text, useMediaQuery, Image, keyframes, Icon, SimpleGrid } from '@chakra-ui/react'
import MainLogo from '../../components/MainLogo'
import LegalFooterLinks from '../../components/_shared/LegalFooterLinks'
import { Outlet } from 'react-router-dom'
import Logo from '../../assets/Logo.png'
import { LuMicroscope, LuActivity, LuBookOpen, LuShieldCheck } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`


const FEATURE_KEYS = [
  { icon: LuMicroscope, key: 'analyses', delay: '0.2s' },
  { icon: LuActivity,   key: 'decision',  delay: '0.35s' },
  { icon: LuBookOpen,   key: 'collaboration', delay: '0.5s' },
  { icon: LuShieldCheck, key: 'pro',      delay: '0.65s' },
]

const STAT_KEYS = ['ai', 'hds', 'pro']

export default function EnterForm() {
  const [isLargerThan992] = useMediaQuery('(min-width: 992px)')
  const { t } = useTranslation()

  return (
    <HStack gap={0} h="100%" position="relative">

      {/* Left — form */}
      <Box
        w={{ base: '100%', lg: '42%' }}
        px={{ base: 4, md: 10 }}
        py={6}
        h="100%"
        overflow="auto"
        bg="white"
        position="relative"
        zIndex={2}
        boxShadow={isLargerThan992 ? '2xl' : 'none'}
        display="flex"
        flexDirection="column"
      >
        {!isLargerThan992 && <MainLogo />}
        <Box flex={1} display="flex" alignItems="center">
          <Outlet />
        </Box>
        <Box pt={4}>
          <LegalFooterLinks />
        </Box>
      </Box>

      {/* Right — brand panel */}
      {isLargerThan992 && (
        <Box
          h="100%"
          flex={1}
          position="relative"
          overflow="hidden"
          bg="#0f0a1e"
        >
          {/* Gradient blobs */}
          <Box position="absolute" top="-120px" right="-80px" w="500px" h="500px" borderRadius="full"
            bg="radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 70%)" pointerEvents="none" />
          <Box position="absolute" bottom="-100px" left="-60px" w="400px" h="400px" borderRadius="full"
            bg="radial-gradient(circle, rgba(219,39,119,0.25) 0%, transparent 70%)" pointerEvents="none" />
          <Box position="absolute" top="40%" left="30%" w="300px" h="300px" borderRadius="full"
            bg="radial-gradient(circle, rgba(79,70,229,0.2) 0%, transparent 70%)" pointerEvents="none" />

          {/* Content */}
          <Box
            position="absolute"
            inset={0}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            px={{ lg: 12, xl: 16 }}
            py={12}
            zIndex={1}
          >
            {/* Logo */}
            <Box
              mb={10}
              sx={{ animation: `${fadeInUp} 0.6s ease-out` }}
            >
              <Image
                src={Logo}
                alt="Careflai"
                maxW="160px"
                filter="brightness(0) invert(1)"
                opacity={0.95}
              />
            </Box>

            {/* Headline */}
            <VStack align="start" spacing={3} mb={10}
              sx={{ animation: `${fadeInUp} 0.6s ease-out 0.1s backwards` }}>
              <Text
                fontSize={{ lg: '3xl', xl: '4xl' }}
                fontWeight="800"
                color="white"
                lineHeight="1.15"
                letterSpacing="-0.02em"
              >
                {t('loginPanel.headline1')}<br />
                {t('loginPanel.headline2')}<br />
                <Box as="span" bgGradient="linear(90deg, #a78bfa, #ec4899)" bgClip="text">
                  {t('loginPanel.headline3')} {t('loginPanel.headlineAccent')}
                </Box>
              </Text>
              <Text fontSize="sm" color="whiteAlpha.600" maxW="360px" lineHeight="1.7">
                {t('loginPanel.subtitle')}
              </Text>
            </VStack>

            {/* Feature cards */}
            <SimpleGrid columns={2} spacing={3} mb={10}>
              {FEATURE_KEYS.map(({ icon, key, delay }) => (
                <Box
                  key={key}
                  bg="rgba(255,255,255,0.05)"
                  backdropFilter="blur(12px)"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  borderRadius="xl"
                  p={4}
                  sx={{ animation: `${fadeInUp} 0.6s ease-out ${delay} backwards` }}
                  _hover={{ bg: 'rgba(255,255,255,0.08)', borderColor: 'whiteAlpha.200' }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2.5} mb={2}>
                    <Box
                      w="30px" h="30px" borderRadius="lg"
                      bg="rgba(167,139,250,0.2)"
                      display="flex" alignItems="center" justifyContent="center"
                      flexShrink={0}
                    >
                      <Icon as={icon} boxSize={3.5} color="purple.300" />
                    </Box>
                    <Text fontSize="sm" fontWeight="700" color="white">
                      {t(`loginPanel.features.${key}.title`)}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="whiteAlpha.600" lineHeight="1.5">
                    {t(`loginPanel.features.${key}.desc`)}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>

            {/* Stats bar */}
            <Box sx={{ animation: `${fadeInUp} 0.6s ease-out 0.8s backwards` }}>
              <HStack spacing={0}>
                {STAT_KEYS.map((key, i) => (
                  <HStack key={key} spacing={0} flex={1}>
                    {i > 0 && <Box w="1px" bg="whiteAlpha.100" h="32px" flexShrink={0} />}
                    <Box flex={1} textAlign="center" px={4}>
                      <Text fontSize="lg" fontWeight="800" color="white" lineHeight="1">
                        {t(`loginPanel.stats.${key}.value`)}
                      </Text>
                      <Text fontSize="2xs" color="whiteAlpha.500" mt={1} letterSpacing="0.04em">
                        {t(`loginPanel.stats.${key}.label`)}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </HStack>
            </Box>
          </Box>
        </Box>
      )}
    </HStack>
  )
}
